/**
 * useNavigationHistory — saves the current path to localStorage.
 *
 * On re-auth (token expiry), the user's current route is preserved
 * by React Router. But if the user was signed out and returns to the
 * root URL, we can redirect them to their last view.
 *
 * This hook persists the current pathname on every navigation change,
 * plus a safelisted subset of the query string (currently just `?tid=`,
 * the open task — see `useActiveTask.ts` — MOB-08) so a restored session
 * reopens the task the user had open. Other query params (e.g. `setting`
 * / `showSettingMenu` for the Settings modal, `q` for in-page search) are
 * intentionally NOT persisted — those are transient UI overlays, not
 * "where the user was", and restoring them would pop the Settings modal
 * open on next cold start. See `RESTORABLE_SEARCH_PARAMS` below.
 *
 * Also maintains a "recent" list (last 8 unique paths) for the
 * QuickFind modal's empty state. That list intentionally stores the
 * bare pathname (no query string) — it represents distinct *views*,
 * not open-task state, and `SearchModal.tsx` matches entries against
 * `useLocation().pathname` to exclude the current page.
 */

import { useEffect } from "react"
import { useLocation } from "react-router-dom"

/** localStorage key for last visited path. */
export const NAV_LAST_URL_KEY = "totodo_nav_last_url"

/** localStorage key for recent navigation history. */
export const NAV_RECENT_KEY = "totodo_nav_recent"

/** Max items in the recent list. */
const MAX_RECENT = 8

/** Paths that should NOT be persisted as "last visited" or in recent list. */
const EXCLUDED_PATHS = ["/logout", "/settings", "/search"]

/**
 * Query params worth restoring alongside the pathname. Only `tid` (the
 * open task, MOB-08) qualifies — everything else the app puts in the URL
 * (`setting`, `showSettingMenu`, `q`, ...) is transient UI state that
 * should not reappear on a restored session. Safelist, not a denylist,
 * so a new query param added elsewhere doesn't leak into persistence
 * by default.
 */
const RESTORABLE_SEARCH_PARAMS = ["tid"]

/** Build the `?...` suffix (or "") containing only restorable params. */
function buildRestorableSearch(search: string): string {
  const params = new URLSearchParams(search)
  const restorable = new URLSearchParams()
  for (const key of RESTORABLE_SEARCH_PARAMS) {
    const value = params.get(key)
    if (value) restorable.set(key, value)
  }
  const qs = restorable.toString()
  return qs ? `?${qs}` : ""
}

/**
 * Get the last visited URL from localStorage.
 * Returns null if not set or if it's the bare root path (nothing to
 * restore — the app already lands there by default). A root path with
 * a query string (e.g. `/?tid=abc`) is still returned, since restoring
 * it reopens the task.
 */
export function getLastVisitedUrl(): string | null {
  const url = localStorage.getItem(NAV_LAST_URL_KEY)
  if (!url || url === "/") return null
  return url
}

/**
 * Navigation history entry stored in the recent list.
 */
export interface RecentNavEntry {
  /** Route path (e.g., "/today", "/project/abc123") */
  path: string
  /** Timestamp of last visit */
  visitedAt: number
}

/**
 * Get the recent navigation history.
 * Returns most-recently-visited first.
 */
export function getRecentNavigation(): RecentNavEntry[] {
  try {
    const raw = localStorage.getItem(NAV_RECENT_KEY)
    if (!raw) return []
    return JSON.parse(raw) as RecentNavEntry[]
  } catch {
    return []
  }
}

/**
 * Push a path to recent navigation history.
 * Deduplicates (moves existing entry to front).
 */
function pushRecentNavigation(path: string): void {
  const recent = getRecentNavigation()
  // Remove existing entry for this path
  const filtered = recent.filter((e) => e.path !== path)
  // Add to front
  filtered.unshift({ path, visitedAt: Date.now() })
  // Trim to max
  const trimmed = filtered.slice(0, MAX_RECENT)
  localStorage.setItem(NAV_RECENT_KEY, JSON.stringify(trimmed))
}

/** Normalize legacy "/today" alias to canonical "/". */
function normalizePath(p: string): string {
  if (p === "/today") return "/"
  return p
}

export function useNavigationHistory(): void {
  const { pathname, search } = useLocation()

  useEffect(() => {
    // Don't persist excluded paths
    if (EXCLUDED_PATHS.some((p) => pathname.startsWith(p))) return

    const normalized = normalizePath(pathname)
    // NAV_LAST_URL_KEY carries the safelisted query params (?tid=...) so
    // a restored session reopens the active task, not just the view.
    localStorage.setItem(
      NAV_LAST_URL_KEY,
      normalized + buildRestorableSearch(search),
    )
    // The recent list stays pathname-only — see doc comment above.
    pushRecentNavigation(normalized)
  }, [pathname, search])
}
